import { useAuth } from '@/hooks/useAuth';
import { useLocationZip } from '@/hooks/useLocationZip';
import {
  AnalyticsEvent,
  handleActionTracking,
} from '@/lib/handleActionTracking';
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';

export const AnalyticsContext = createContext<{
  handleActionTracking: (event: AnalyticsEvent) => void;
}>({ handleActionTracking: () => {} });

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const { locationAddress } = useLocationZip();

  const memoizedHandleActionTracking = useCallback(
    (event: AnalyticsEvent) => {
      handleActionTracking({
        username: user?.email,
        zip: locationAddress,
        ...event,
      });
    },
    [user, locationAddress]
  );

  const value = useMemo(
    () => ({
      handleActionTracking: memoizedHandleActionTracking,
    }),
    [memoizedHandleActionTracking]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
